import React, { useState, useEffect } from 'react';
import { Result, Button, Card, Space, Typography, Tag, Spin, Alert } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PrefixPath from '../../../config/AppConfig';
import Utils from '../../../utils/Utils';

const { Title, Text, Paragraph } = Typography;

/* eslint-disable react/prop-types */
const ProcessInvoicesStep = ({ batchId, batchGuid, totalInvoices, systemError, onReset }) => {
  const navigate = useNavigate();
  const [processingStatus, setProcessingStatus] = useState('created');

  useEffect(() => {
    // Simulate processing status update
    const timer = setTimeout(() => {
      setProcessingStatus('ready');
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleViewBatch = () => {
    navigate(`${PrefixPath.APP_PREFIX_PATH}/vms-invoice/batches/${batchId}`);
  };

  const handleViewAllBatches = () => {
    navigate(`${PrefixPath.APP_PREFIX_PATH}/vms-invoice/batches`);
  };

  if (systemError) {
    const {
      tooLarge,
      messages,
      details,
      successfulUploads,
      failedUploads,
      notProcessedUploads
    } = systemError;
    const uploadedSomething = successfulUploads > 0;

    return (
      <div className="process-invoices-step">
        <Result
          status="warning"
          title={tooLarge ? 'Batch Too Large for VMS' : 'Batch Could Not Be Sent to VMS'}
          subTitle={
            <Space direction="vertical" size="small">
              <Text>
                {uploadedSomething
                  ? 'Part of this batch was uploaded before the VMS system stopped accepting it.'
                  : 'The VMS system did not accept this batch.'}
              </Text>
              {batchGuid && <Text type="secondary">Batch GUID: {batchGuid}</Text>}
            </Space>
          }
          extra={[
            batchId && (
              <Button
                data-testid="vmsinvoice-view-batch-details-btn"
                type="primary"
                key="view"
                icon={<FileTextOutlined />}
                onClick={handleViewBatch}
              >
                View Batch Details
              </Button>
            ),
            <Button
              data-testid="vmsinvoice-view-all-batches-btn"
              key="list"
              onClick={handleViewAllBatches}
            >
              View All Batches
            </Button>,
            <Button
              data-testid="vmsinvoice-step-upload-new-btn"
              key="new"
              type={batchId ? 'default' : 'primary'}
              onClick={onReset}
            >
              Upload New Batch
            </Button>
          ].filter(Boolean)}
        >
          <Card style={{ marginTop: 24, textAlign: 'left' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Alert
                data-testid="vmsinvoice-system-error-alert"
                message="What went wrong"
                description={
                  <div>
                    {messages.map((text, i) => (
                      <Paragraph key={i} style={{ marginBottom: 8 }}>
                        {text}
                      </Paragraph>
                    ))}
                    {details.length > 0 && (
                      <ul style={{ marginBottom: 0 }}>
                        {details.map((detail, i) => (
                          <li key={i}>
                            <Text type="secondary">{detail}</Text>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                }
                type="warning"
                showIcon
              />

              <div>
                <Title level={5}>Where This Batch Stands</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Invoices submitted:</Text>
                    <Tag color="blue">{totalInvoices}</Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Uploaded successfully:</Text>
                    <Tag color="green">{successfulUploads}</Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Not processed:</Text>
                    <Tag>{notProcessedUploads}</Tag>
                  </div>
                  {failedUploads > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Rejected by VMS:</Text>
                      <Tag color="red">{failedUploads}</Tag>
                    </div>
                  )}
                </Space>
              </div>

              {notProcessedUploads > 0 && (
                <Alert
                  message="Next Steps"
                  description={
                    <div>
                      <Paragraph style={{ marginBottom: 8 }}>
                        <InfoCircleOutlined />{' '}
                        {`${notProcessedUploads} invoice(s) were never processed by VMS. They were not rejected - they are still valid and can be re-submitted.`}
                      </Paragraph>
                      {tooLarge && (
                        <Paragraph style={{ marginBottom: 0 }}>
                          Split them into a smaller CSV file and upload that as a new batch.
                        </Paragraph>
                      )}
                      {uploadedSomething && (
                        <Paragraph style={{ marginBottom: 0 }}>
                          Do not re-submit the {successfulUploads} invoice(s) that already
                          uploaded - open the batch details to see exactly which ones those are.
                        </Paragraph>
                      )}
                    </div>
                  }
                  type="info"
                  showIcon
                />
              )}
            </Space>
          </Card>
        </Result>
      </div>
    );
  }

  if (processingStatus === 'created') {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 20 }}>Creating Batch...</Title>
        <Text type="secondary">Please wait while we create your invoice batch</Text>
      </div>
    );
  }

  return (
    <div className="process-invoices-step">
      <Result
        status="success"
        icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
        title="Batch Created Successfully!"
        subTitle={
          <Space direction="vertical" size="small">
            <Text>Your invoice batch has been created with &apos;pre&apos; status</Text>
            <Text type="secondary">Batch GUID: {batchGuid}</Text>
          </Space>
        }
        extra={[
          <Button
            data-testid="vmsinvoice-view-batch-details-btn"
            type="primary"
            key="view"
            icon={<FileTextOutlined />}
            onClick={handleViewBatch}
          >
            View Batch Details
          </Button>,
          <Button
            data-testid="vmsinvoice-view-all-batches-btn"
            key="list"
            onClick={handleViewAllBatches}
          >
            View All Batches
          </Button>,
          <Button
            data-testid="vmsinvoice-step-upload-new-btn"
            key="new"
            onClick={onReset}
          >
            Upload New Batch
          </Button>
        ]}
      >
        <Card style={{ marginTop: 24, textAlign: 'left' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={5}>Batch Summary</Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Total Invoices:</Text>
                  <Tag color="blue">{totalInvoices}</Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Status:</Text>
                  <Tag icon={<ClockCircleOutlined />} color="orange">Pre (Pending)</Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Created:</Text>
                  <Text type="secondary">{Utils.formatDateTimeUSFullDate(new Date())}</Text>
                </div>
              </Space>
            </div>

            <Alert
              message="Next Steps"
              description={
                <div>
                  <Paragraph>
                    <InfoCircleOutlined /> Your invoices have been uploaded with &apos;pre&apos; status. 
                    They are ready to be processed.
                  </Paragraph>
                  <ul style={{ marginBottom: 0 }}>
                    <li>Click &quot;View Batch Details&quot; to see all invoices in this batch</li>
                    <li>You can process invoices individually or in bulk</li>
                    <li>Monitor the status of each invoice after processing</li>
                    <li>Reprocess any failed invoices if needed</li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
            />

            <Alert
              message="Processing Information"
              description={
                <Space direction="vertical">
                  <Text>
                    • Invoices with &apos;pre&apos; status need to be manually processed
                  </Text>
                  <Text>
                    • Processing will send invoices to the VMS system
                  </Text>
                  <Text>
                    • You&apos;ll need to provide vendor credentials when processing
                  </Text>
                  <Text>
                    • Each invoice status will update to &apos;Success&apos; or &apos;Failed&apos; after processing
                  </Text>
                </Space>
              }
              type="warning"
              showIcon
            />
          </Space>
        </Card>
      </Result>
    </div>
  );
};

export default ProcessInvoicesStep;
